import React, { useState, useCallback } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/frontend/components/ui/avatar";
import { getInitials, getAvatarColor } from "@/frontend/lib/avatar-utils";
import { Users } from "lucide-react";
import { FaInstagram, FaTelegramPlane, FaWhatsapp } from "react-icons/fa";
import { cn } from "@/frontend/lib/utils";
import { WhatsAppBusinessIcon } from "@/frontend/components/WhatsAppBusinessIcon";

interface ConversationAvatarProps {
  name: string;
  avatarUrl?: string | null;
  isGroup?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  /** Show a ring around the avatar */
  showRing?: boolean;
  /** Platform identifier (e.g. "telegram", "whatsapp") */
  platform?: string;
}

const sizeClasses = {
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-sm",
};

const iconSizes = {
  sm: "w-3.5 h-3.5",
  md: "w-4 h-4",
  lg: "w-5 h-5",
};

/**
 * Reusable avatar component for conversations
 * - Shows image if avatarUrl is provided with smooth loading transition
 * - Falls back to initials for DMs or Users icon for groups
 * - Memoized to prevent unnecessary re-renders
 */
const badgeSizes = {
  sm: "w-3.5 h-3.5 -bottom-0.5 -right-0.5",
  md: "w-4 h-4 -bottom-0.5 -right-0.5",
  lg: "w-5 h-5 -bottom-0.5 -right-0.5",
};

const badgeIconSizes = {
  sm: "w-2 h-2",
  md: "w-2.5 h-2.5",
  lg: "w-3 h-3",
};

export const ConversationAvatar = React.memo(function ConversationAvatar({
  name,
  avatarUrl,
  isGroup = false,
  size = "md",
  className,
  showRing = false,
  platform,
}: ConversationAvatarProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const initials = getInitials(name);
  const colorClass = getAvatarColor(name);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(false);
  }, []);

  // Reset state when avatarUrl changes
  React.useEffect(() => {
    if (avatarUrl) {
      setImageLoaded(false);
      setImageError(false);
    }
  }, [avatarUrl]);

  const shouldShowImage = avatarUrl && !imageError;

  const isTelegram = platform === "telegram";
  const isInstagram = platform === "instagram";
  const isWhatsapp = platform === "whatsapp";
  const isWaba = platform === "waba";

  return (
    <div className="relative inline-flex">
      <Avatar
        className={cn(
          sizeClasses[size],
          showRing && "ring-2 ring-white shadow-sm",
          "transition-shadow duration-200",
          className
        )}
      >
        {shouldShowImage && (
          <AvatarImage
            src={avatarUrl}
            alt={name}
            className={cn(
              "object-cover transition-opacity duration-200",
              imageLoaded ? "opacity-100" : "opacity-0"
            )}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        )}
        <AvatarFallback
          className={cn(
            "font-semibold transition-opacity duration-200",
            colorClass,
            // Show fallback immediately if no image, or while image is loading
            shouldShowImage && imageLoaded ? "opacity-0" : "opacity-100"
          )}
        >
          {isGroup ? (
            <Users className={cn(iconSizes[size], "opacity-70")} />
          ) : (
            initials
          )}
        </AvatarFallback>
      </Avatar>
      {isTelegram && (
        <span
          className={cn(
            "absolute flex items-center justify-center rounded-full bg-[#2AABEE] text-white ring-2 ring-white",
            badgeSizes[size]
          )}
        >
          <FaTelegramPlane className={badgeIconSizes[size]} />
        </span>
      )}
      {isInstagram && (
        <span
          className={cn(
            "absolute flex items-center justify-center rounded-full bg-[#E4405F] text-white ring-2 ring-white",
            badgeSizes[size]
          )}
        >
          <FaInstagram className={badgeIconSizes[size]} />
        </span>
      )}
      {isWaba && (
        <span
          className={cn(
            "absolute flex items-center justify-center rounded-full bg-[#25D366] text-white ring-2 ring-white",
            badgeSizes[size]
          )}
        >
          <WhatsAppBusinessIcon className={cn(badgeIconSizes[size], "text-white")} />
        </span>
      )}
      {isWhatsapp && (
        <span
          className={cn(
            "absolute flex items-center justify-center rounded-full bg-[#25D366] text-white ring-2 ring-white",
            badgeSizes[size]
          )}
        >
          <FaWhatsapp className={badgeIconSizes[size]} />
        </span>
      )}
    </div>
  );
});
